import { useEffect, useState } from "react";
import {
  ApiError,
  authMe,
  createUser,
  disableUser,
  listUsers,
  resetUserPassword,
  updateUser,
  type ConsoleAuthState,
  type ConsoleUserDto,
} from "../lib/api";
import {
  EmptyState,
  ErrorState,
  Modal,
  PageHeader,
  SkeletonRows,
  StatusBadge,
} from "../components/ui";
import type { HealthTone } from "../lib/provider-display";
import { formatTimestamp } from "../lib/format";

const ROLE_TONE: Record<string, HealthTone> = {
  owner: "ok",
  admin: "ok",
  developer: "muted",
  viewer: "muted",
  billing: "muted",
};

function randomPassword(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const bytes = new Uint32Array(20);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => alphabet[b % alphabet.length]).join("");
}

export default function UsersPage() {
  const [auth, setAuth] = useState<ConsoleAuthState | null>(null);
  const [users, setUsers] = useState<ConsoleUserDto[]>([]);
  const [roles, setRoles] = useState<Array<{ role: string; description: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ username: "", displayName: "", role: "viewer", password: "" });
  const [busy, setBusy] = useState(false);

  const [resetFor, setResetFor] = useState<ConsoleUserDto | null>(null);
  const [resetPassword, setResetPassword] = useState("");

  async function load() {
    try {
      // Identity first: a role without manage_users must not even call the
      // operator API (the server would answer 403 and the console would log noise).
      const me = await authMe();
      setAuth(me);
      if (!me.user?.capabilities?.includes("manage_users")) {
        setUsers([]);
        setRoles([]);
        setError(null);
        return;
      }
      const list = await listUsers();
      setUsers(list.data);
      setRoles(list.roles);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load operators");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function run(action: () => Promise<unknown>, message: string) {
    setBusy(true);
    setNotice(null);
    try {
      await action();
      setNotice(message);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  const canManage = Boolean(auth?.user?.capabilities?.includes("manage_users"));
  const meId = auth?.user?.id;

  return (
    <div className="mx-auto max-w-5xl pb-16">
      <PageHeader
        title="Operators"
        subtitle="Accounts that can sign in to this console, and what each role may do."
        right={
          canManage ? (
            <button
              className="btn-primary"
              onClick={() => {
                setDraft({ username: "", displayName: "", role: "viewer", password: randomPassword() });
                setCreateOpen(true);
              }}
            >
              New operator
            </button>
          ) : null
        }
      />

      {error && (
        <div className="mb-4">
          <ErrorState message={error} onRetry={load} />
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-accent">
          {notice}
        </div>
      )}
      {loading && <SkeletonRows rows={4} height={56} />}

      {!loading && !canManage && (
        <EmptyState
          title="Read-only access"
          body="Your role can see the console but not manage operators. Ask an owner to change your role if you need this."
        />
      )}

      {!loading && canManage && (
        <section className="panel overflow-x-auto">
          <div className="panel-header">
            <h2 className="text-sm font-semibold">Accounts</h2>
            <span className="chip-muted">{users.length} operators</span>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-canvas-border">
                <th className="table-th">Operator</th>
                <th className="table-th">Role</th>
                <th className="table-th">Status</th>
                <th className="table-th">Last sign-in</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-canvas-border">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="table-td">
                    <div className="font-medium">{u.username}</div>
                    <div className="text-xs text-canvas-muted">
                      {u.displayName}
                      {u.id === meId ? " · you" : ""}
                    </div>
                  </td>
                  <td className="table-td">
                    {u.id === meId ? (
                      <StatusBadge label={u.role} tone={ROLE_TONE[u.role] ?? "muted"} />
                    ) : (
                      <select
                        className="input !w-auto !py-1 text-xs"
                        value={u.role}
                        disabled={busy}
                        onChange={(e) => void run(() => updateUser(u.id, { role: e.target.value }), `${u.username} is now ${e.target.value}`)}
                      >
                        {(roles.length ? roles.map((r) => r.role) : [u.role]).map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="table-td">
                    <StatusBadge
                      label={u.status}
                      tone={u.status === "active" ? "muted" : "danger"}
                    />
                  </td>
                  <td className="table-td mono text-xs text-canvas-muted">
                    {u.lastLoginAt ? formatTimestamp(u.lastLoginAt) : "never"}
                  </td>
                  <td className="table-td">
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        className="btn-ghost !px-2 !py-1 !text-xs"
                        disabled={busy}
                        onClick={() => {
                          setResetFor(u);
                          setResetPassword(randomPassword());
                        }}
                      >
                        Reset password
                      </button>
                      {u.status === "active" ? (
                        <button
                          className="btn-ghost !px-2 !py-1 !text-xs"
                          disabled={busy || u.id === meId}
                          onClick={() => void run(() => disableUser(u.id), `${u.username} disabled and signed out`)}
                        >
                          Disable
                        </button>
                      ) : (
                        <button
                          className="btn-ghost !px-2 !py-1 !text-xs"
                          disabled={busy}
                          onClick={() => void run(() => updateUser(u.id, { status: "active" }), `${u.username} re-enabled`)}
                        >
                          Enable
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {!loading && canManage && roles.length > 0 && (
        <section className="panel mt-6 p-4">
          <h2 className="mb-3 text-sm font-semibold">Roles</h2>
          <ul className="space-y-2">
            {roles.map((r) => (
              <li key={r.role} className="flex items-start gap-3 text-sm">
                <span className="w-24 shrink-0">
                  <StatusBadge label={r.role} tone={ROLE_TONE[r.role] ?? "muted"} />
                </span>
                <span className="text-canvas-muted">{r.description}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {createOpen && (
        <Modal title="New operator" onClose={() => setCreateOpen(false)}>
          <div className="space-y-4">
            <div>
              <label className="label">Username</label>
              <input
                className="input"
                value={draft.username}
                placeholder="sara"
                autoFocus
                onChange={(e) => setDraft({ ...draft, username: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Display name (optional)</label>
              <input
                className="input"
                value={draft.displayName}
                placeholder="Sara A."
                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Role</label>
              <select
                className="input"
                value={draft.role}
                onChange={(e) => setDraft({ ...draft, role: e.target.value })}
              >
                {roles.map((r) => (
                  <option key={r.role} value={r.role}>
                    {r.role} — {r.description}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Temporary password</label>
              <div className="flex gap-2">
                <input
                  className="input mono"
                  value={draft.password}
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                />
                <button
                  className="btn-ghost shrink-0"
                  type="button"
                  onClick={() => setDraft({ ...draft, password: randomPassword() })}
                >
                  Generate
                </button>
              </div>
              <p className="mt-2 text-xs text-canvas-muted">
                Share it once; the operator can change it under Settings → Account.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-ghost" onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={busy || !draft.username || draft.password.length < 10}
                onClick={() =>
                  void run(async () => {
                    await createUser(draft);
                    setCreateOpen(false);
                    setNotice(`Operator ${draft.username} created`);
                  }, "Operator created")
                }
              >
                Create operator
              </button>
            </div>
          </div>
        </Modal>
      )}

      {resetFor && (
        <Modal title={`Reset password — ${resetFor.username}`} onClose={() => setResetFor(null)}>
          <div className="space-y-4">
            <p className="text-sm text-canvas-muted">
              All of this operator's active sessions will be signed out.
            </p>
            <div>
              <label className="label">New password</label>
              <div className="flex gap-2">
                <input
                  className="input mono"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                />
                <button
                  className="btn-ghost shrink-0"
                  type="button"
                  onClick={() => setResetPassword(randomPassword())}
                >
                  Generate
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setResetFor(null)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={busy || resetPassword.length < 10}
                onClick={() =>
                  void run(async () => {
                    await resetUserPassword(resetFor.id, resetPassword);
                    setResetFor(null);
                  }, `Password reset for ${resetFor.username}`)
                }
              >
                Reset password
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
